import { AssetTypes, ImageAspectRatio } from './Common';
import { AiringTime, PlayOptionsData, Recordable, Rentable, Subscribable, TVEpisodeInfo } from './CommonTypes';
import { VodPlayerAssetInfo } from './Player';
import { VODAssetEntitlementState } from './Entitlement';

export enum AudioMixType {
    NO_SOUND = '1',
    MONO = '2',
    STEREO = '3',
    DOLBY_5_1 = '5',
    DOLBY_DIGITAL = '10',
    DOLBY_PROLOGIC = '11',
}

export enum PersonRole {
    ACTOR = 'Actor',
    DIRECTOR = 'Director',
}

export type Marker = {
    value: string;
    backgroundColor?: string;
    textColor?: string;
    priority?: number;
    icon?: any;
};

export enum ResolutionType {
    SD = 'SD',
    HD = 'HD',
    '4K' = '4K',
    '8K' = '8K',
}

export type Bookmark = {
    titleId: string;
    bookmark: number;
};

export type PlaceholderAsset = {
    id: string;
    type: AssetTypes;
    isPortrait: boolean;
};

export type ChannelCapabilities = {
    live: boolean;
    instant_restart: boolean;
    replay: boolean;
    replay_depth: number | null;
    timeshift: boolean;
    timeshift_depth: number | null;
    recording: boolean;
    recording_depth: number | null;
    recording_expires_after: number | null;
};

export type ChannelItem = ChannelCapabilities & {
    max: ChannelCapabilities;
    id: string;
    number: number;
    name: string;
    max_quality: string;
    logo_url: string;
    logo_url_dark: string;
    logo_url_light: string;
    adult_content: boolean;
    drm_required: boolean;
    additional_services_available: boolean;
    supported_drm_options: string[];
    supported_stream_formats: string[];
};

export type ChannelsResponse = {
    results: ChannelItem[];
    count: number;
    pages: number;
};

export type Channel = {
    id: string;
    logo: string;
    number: number;
    name: string;
    type: AssetTypes;
    subscription?: boolean;
    favorite?: boolean;
    isHd?: boolean;
    replay: boolean;
    replay_depth: number | null;
    live: boolean;
    instant_restart: boolean;
    timeshift: boolean;
    timeshift_depth: number | null;
    drm_required: boolean;
    supported_stream_formats: string[];
    additional_services_available?: boolean;
    recording: boolean;
    recording_depth: number | null;
};

export type ChannelGroup = {
    id: string;
    title: string;
    channel_ids: string[];
};

export type ChannelGroupsResponse = {
    channel_groups: ChannelGroup[];
};

export type Broadcast = AiringTime &
    TVEpisodeInfo &
    Subscribable &
    Bookmark & {
        id: string;
        program_id?: string;
        title: string;
        image: string;
        image_url: string;
        landscapeImage: string;
        metaData: string;
        countries: string[];
        duration: number;
        releaseYear: string;
        genres: string[];
        audioMixType: AudioMixType;
        hasSubtitle: boolean;
        hasAudioLanguage: boolean;
        channelHasReplay: boolean;
        listings: Listing[];
        vod_terms: VodTerms[];
        isChannelSubscribed: boolean;
        isLive: boolean;
        channelId: string;
        programSeriesId: string;
        channelName?: string;
        channelLogo?: string;
        isHDChannel?: boolean;
        episodeNumber?: number;
        type: AssetTypes;
        isFavorite?: boolean;
        isPortrait: boolean;
        isMovie?: boolean;
        worldId?: string;
        vod_series_id?: string;
        program?: Program;
        vodAssetState?: string;
        additional_services_available: boolean;
    };

export type NowAndNext = AiringTime &
    TVEpisodeInfo &
    Subscribable & {
        id: string;
        channelId: string;
        channelLogo?: string;
        channelName: string;
        channelNumber?: number;
        isChannelSubscribed: boolean;
        image: string;
        startTime: number;
        endTime: number;
        type: AssetTypes;
        title: string;
        seriesId: string | null;
        episodeId: string;
        isLive: boolean;
        program: NowAndNextProgram['program'];
        duration: number;
        nextBroadcastTitle: string;
        progress: number;
        bookmark: number;
        next_request_in: number;
        playOptions: any;
        items: any[];
    };

export type ContinueWatchingContent = {
    id: string;
    title: string;
    episode_title?: string;
    episode_id?: string;
    description: string;
    tagline?: string;
    image_url?: string;
    kind?: string | null;
    program_series_id?: string;
    vod_series_id?: string;
    season_number?: number;
    episode_number?: number;
    category?: string;
    genres?: string[];
    production_countries?: string[];
    imdb_rating?: number | null;
    release_year?: number;
    title_original?: string;
};

export type ContinueWatchingVOD = ContinueWatchingContent & {
    duration: number;
    adult_content?: boolean;
    has_trailer?: boolean;
    max_quality?: string;
    vod_series_id: string;
    vod_terms?: VodTerm[];
};

export type ContinueWatchingListing = {
    id: string;
    channel_id: string;
    start: number;
    duration: number;
    outdated?: boolean;
    program: ContinueWatchingContent;
};

export type ContinueWatching = {
    results: Array<{
        type: 'VOD_ASSET' | 'LISTING';
        title: string;
        image_url: string;
        item: ContinueWatchingVOD | ContinueWatchingListing;
    }>;
    image_aspect_ratio: string;
    count: number;
    pages: number;
};

export type ContinueWatchingItems = {
    id: string;
    title: string;
    episode_title?: string;
    episode_id?: string;
    description: string;
    tagline?: string;
    image: string;
    kind: string | null;
    program_series_id?: string;
    vod_series_id?: string;
    season_number?: number;
    episode_number?: number;
    genres: string[];
    production_countries: string[];
    channel_id?: string;
    duration: number;
    type: AssetTypes;
    originalType: 'VOD_ASSET' | 'LISTING';
    vod_terms?: VodTerm[];
    isWatchable?: boolean;
};

export type MovieCard = Subscribable & {
    id: string;
    titleId: string;
    type: AssetTypes;
    category: string;
    title: string;
    image: string;
    image_url: string;
    landscapeImage: string;
    metaData: string;
    countries: string[];
    genres: string[];
    releaseYear: string | number;
    duration: number;
    bookmark?: number;
    firstAvailability?: number;
    imdbRating?: number | null;
    price: number;
    vodAssetState: VODAssetEntitlementState;
    isPortrait: boolean;
    audio_languages?: string[];
    subtitle_languages?: string[];
    vod_terms?: VodTerm[];
    entitlementEnd?: number;
    watchPeriod?: number;
    createdAt?: number;
    isWatchable?: boolean;
};

export type Movie = Rentable &
    Bookmark & {
        id: string;
        title: string;
        image: string;
        image_url: string;
        landscapeImage: string;
        metaData: string;
        countries: string[];
        releaseYear: string;
        duration: number;
        genres: string[];
        firstAvailability: number;
        price: number;
        type: AssetTypes;
        category: AssetTypes;
        replay_until?: number;
        imdbRating?: number;
        isPortrait: boolean;
        entitlementEnd?: number;
        audio_languages: string[];
        subtitle_languages: string[];
        vod_series_id?: string;
        kind?: string;
        watchPeriod?: number;
        createdAt?: number;
        vod_terms: VodTerm[];
        listings?: ListingMovie[];
        additional_services_available?: boolean;
        vod_session_until?: string;
    };

export type Series = {
    id: string;
    title: string;
    image: string;
    landscapeImage: string;
    customImage?: string;
    imageAspectRatio?: ImageAspectRatio;
    metaData: string;
    genres: string[];
    countries?: string[];
    releaseYear?: string;
    firstAvailability: number;
    type: AssetTypes;
    imdbRating?: number;
    lastEpisodeFirstAvailability?: number;
    isVOD: boolean;
    price: number;
    channelId?: string;
};

export type Program = {
    id: string;
    title: string;
    episode_title?: string;
    description?: string;
    tagline?: string;
    image: string;
    landscapeImage: string;
    kind?: string;
    program_series_id?: string;
    season_number?: number;
    episode_number?: number;
    category?: string;
    metaData: string;
    countries: string[];
    releaseYear: string;
    genres: string[];
    audioMixType: AudioMixType;
    hasSubtitle: boolean;
    hasAudioLanguage: boolean;
    numberOfSeasons: number;
    firstAvailability: number;
    price: number;
    type: AssetTypes;
    imdbRating?: number;
    lastEpisodeFirstAvailability?: number;
    isVOD: boolean;
    production_countries?: string[];
};

export type ProgramSeries = {
    id: string;
    title: string;
    image: string;
    landscapeImage: string;
    metaData: string;
    countries: string[];
    releaseYear: string;
    genres: string[];
    audioMixType: AudioMixType;
    hasSubtitle: boolean;
    hasAudioLanguage: boolean;
    numberOfSeasons: number;
    firstAvailability: number;
    price: number;
    type: AssetTypes;
    imdbRating?: number;
    lastEpisodeFirstAvailability?: number;
    isVOD: boolean;
};

export type Season<T extends DetailEpisode | TVSeriesDetailEpisode> = {
    id: string;
    name: string;
    numberOfEpisodes: number;
    number: number;
    episodes?: T[];
};

export type EpisodeCard = {
    id: string;
    titleId: string;
    episodeId: string;
    episode_number: number;
    season_number: number;
    type: AssetTypes;
    kind: AssetTypes;
    title: string;
    episode_title?: string;
    isPortrait: boolean;
    image: string;
    image_url: string;
    landscapeImage: string;
    description: string;
    releaseYear: string;
    release_year: string;
    countries: string[];
    production_countries: string[];
    genres: string[];
    duration: number;
    bookmark: number;
    imdbRating?: number | null;
    price: number;
    firstAvailability: number;
    vodAssetState: VODAssetEntitlementState;
    audio_languages: string[];
    subtitle_languages: string[];
    vod_series_id?: string;
    vod_terms: VodTerm[];
    vod_session_until?: number | null;
    isWatchable?: boolean;
};

export type Episode = Rentable &
    Bookmark & {
        id: string;
        episodeId: string;
        episode_number: number;
        season_number: number;
        seasonNumber?: number;
        title: string;
        image: string;
        landscapeImage: string;
        image_url: string;
        countries: string[];
        production_countries: string[];
        releaseYear: string;
        release_year: string;
        duration: number;
        genres: any[];
        firstAvailability: number;
        additional_services_available?: boolean;
        price: number;
        type: AssetTypes;
        kind: AssetTypes;
        isPortrait: boolean;
        imdbRating?: number;
        audio_languages: string[];
        subtitle_languages: string[];
        vod_series_id?: string;
        vod_session_until?: number;
        vod_terms: VodTerm[];
    };

export type DetailEpisode = Episode & {
    episode_title: string;
    episodeTitleShort: string;
    tagline: string;
    longDescription: string;
    description: string;
    background_image_url?: string;
    cast: Person[];
    credits: Person[];
    hasTrailer: boolean;
    captionLanguages?: string[];
    originalLanguages?: string[];
    channelImage?: string;
    channelName?: string;
    seriesTitle?: string;
    kind?: string;
    vod_session_until?: number;
    playOptions: PlayOptionsData;
    program?: Program;
};

export type TVSeriesDetailEpisode = Omit<DetailEpisode, keyof Rentable> &
    AiringTime &
    Subscribable &
    Recordable & {
        eventId?: string;
        channelId?: string;
        channel_id?: string;
        channelImage?: string;
        image?: string;
        image_url?: string;
        listings?: any;
        channelName?: string;
        isHDChannel?: boolean;
        isRecorded: boolean;
        isChannelSubscribed?: boolean;
        channelHasReplay?: boolean;
        playOptions: PlayOptionsData;
        isLive?: boolean;
        program_series_id?: string;
        vod_series_id?: string;
        start: number;
        recording_until: number;
        replay_until: number;
        restart_until: number;
        series_recording_allowed: boolean;
        additional_services_available?: boolean;
    };

export type VODSeriesDetailEpisode = Omit<DetailEpisode, keyof Rentable> &
    AiringTime &
    Subscribable &
    Recordable & {
        eventId?: string;
        channelId?: string;
        channelImage?: string;
        image?: string;
        image_url?: string;
        listings?: Listing[];
        channelName?: string;
        isHDChannel?: boolean;
        isRecorded: boolean;
        isChannelSubscribed?: boolean;
        channelHasReplay?: boolean;
        playOptions: PlayOptionsData;
        isLive?: boolean;
        program_series_id?: string;
        vod_series_id?: string;
        id: string;
        title: string;
        titleId: string;
        episode_title: string;
        description: string;
        tagline: string;
        kind: string;
        season_number: number;
        episode_number: number;
        category: string;
        genres: string[];
        release_year: string;
        duration: number;
        production_countries: string[];
        imdb_rating: number | null;
        has_trailer: boolean;
        max_quality: string;
        audio_languages: string[];
        subtitle_languages: string[];
        type: AssetTypes;
        vod_terms: Array<{
            type: string;
            start: number;
            end: number | null;
            price: number | null;
            currency: string;
            period: number | null;
            rental_period: number | null;
            watch_period: number | null;
            catalog_id: string;
            catalog_name: string;
            id: string;
        }>;
        vod_session_until: number;
        credits: Credit[];
        additional_services_available: boolean;
        vodAssetState: VODAssetEntitlementState;
    };

export type Person = {
    id: string;
    title: string;
    name?: string;
    image: string;
    type: AssetTypes;
    role: PersonRole;
    onClickOpenCollection?: boolean;
    person?: {
        id: string;
        name: string;
        image_url: string;
    };
};

export type Credit = {
    person: {
        id: string;
        name: string;
        image_url?: string;
    };
    title?: string;
    role: string;
};

export type VodTerm = {
    id: string;
    type: string;
    price: number;
    currency: string;
    offerPrice?: number;
    period: number;
    rental_period: number;
    watch_period: number;
    catalog_id: string;
    catalog_name: string;
    start: number;
    end: number;
};

export type VODPurchaseAsset = {
    id: string;
    title: string;
    title_original: string;
    episode_title: string;
    episode_title_original: string;
    description: string;
    tagline: string;
    image_url: string;
    background_image_url: string;
    kind: string;
    vod_series_id: string | null;
    season_number: number | null;
    episode_number: number | null;
    category: string;
    genres: string[];
    release_year: number;
    duration: number;
    production_countries: string[];
    imdb_rating: number | null;
    has_trailer: boolean;
    max_quality: string;
    adult_content: boolean;
};

export type VODPurchase = {
    id: string;
    vod_term: VodTerm;
    vod_asset: VODPurchaseAsset;
    valid_until: number;
    created_at: number;
};

export type VODPurchasesResponse = {
    results: VODPurchase[];
    count: number;
    pages: number;
};

export type MovieApiResponse = {
    id: string;
    title: string;
    title_original: string;
    description: string;
    tagline: string;
    kind: string;
    image_url: string;
    background_image_url: string;
    category: string;
    genres: string[];
    release_year: number;
    duration: number;
    episode_title: string;
    episode_title_original: string;
    episode_number: number | null;
    season_number: number | null;
    vod_series_id: string | null;
    production_countries: string[];
    audio_languages: string[];
    subtitle_languages: string[];
    supported_drm_options: string[];
    supported_stream_formats: string[];
    max_quality: string;
    adult_content: boolean;
    additional_services_available: boolean;
    has_trailer: boolean;
    imdb_rating: number | null;
    vod_session_until: number | null;
    vod_terms: VodTerm[];
    credits: Credit[];
};

export type Listing = {
    id?: string;
    channelId?: string;
    channel_id?: string;
    start?: number;
    startTime?: number;
    endTime?: number;
    duration?: number;
    recordingUntil?: number;
    recording_until?: number;
    replayUntil?: number;
    replay_until?: number;
    restartUntil?: number;
    restart_until?: number;
    recordingId?: string;
    seriesRecordingAllowed?: boolean;
    series_recording_allowed?: boolean;
    channel?: Channel;
    listings?: Listing[];
    bookmark?: number;
    isLive?: boolean;
    category?: string;
    description?: string;
    episodeNumber?: number;
    episodeTitle?: string;
    genres?: string[];
    itemId?: string;
    image_url?: string;
    kind?: string;
    productionCountries?: string[];
    program_id?: string;
    programSeriesId?: string;
    releaseYear?: string | number;
    seasonNumber?: number;
    tagline?: string;
    title?: string;
    type?: string;
    isPortrait?: boolean;
    aspectRatio?: ImageAspectRatio;
};

export type VodTerms = {
    catalog_id: string;
    catalog_name: string;
    currency: string;
    end: number;
    id: string;
    period: number;
    price: number;
    rental_period: number;
    start: number;
    type: string;
    watch_period: number;
};

export type ListingMovie = {
    additional_services_available: boolean;
    adult_content: boolean;
    channel_id: string;
    duration: number;
    id: string;
    next_listing_id: string;
    program: ProgramDetails;
    recording_until: number;
    replay_until: number;
    restart_until: number;
    series_recording_allowed: boolean;
    start: number;
};

export type Resource = {
    id: string;
};

export type Recording = AiringTime &
    Omit<Bookmark, 'titleId'> & {
        id: string;
        recordingId: string;
        title: string;
        duration: number;
        releaseYear?: string;
        eventId?: string;
        genres: string[];
        image: string;
        image_url: string;
        landscapeImage: string;
        type: AssetTypes;
        metaData: string;
        isPortrait?: boolean;
        seriesId?: string;
        isLive?: boolean;
        programSeriesId: string;
        start?: number;
        channelId?: string;
    };

export type CoverAsset = (
    | Broadcast
    | Movie
    | MovieCard
    | Series
    | Episode
    | EpisodeCard
    | Listing
    | Channel
    | NowAndNext
    | Person
    | Resource
    | Recording
    | ContinueWatchingItems
) & {
    genres?: string[];
    channelName?: string;
    channelImage?: string;
    vodAssetState?: string;
    kind?: string;
    type?: string;
    title?: string;
    aspectRatio?: ImageAspectRatio;
    program_series_id?: string;
    vod_series_id?: string;
    vod_terms?: any[];
};

export type StandardAsset = {
    type: AssetTypes;
    title: string;
    id: string;
    landscapeImage: string;
    hasTrailer: boolean;
    captionLanguages?: string[];
    originalLanguages?: string[];
};

export type MovieDetails = StandardAsset &
    Rentable & {
        description: string;
        tagline: string;
        episodeId: string;
        credits: Credit[];
        vod_terms: VodTerm[];
        vod_session_until: number;
        additional_services_available: boolean;
        id: string;
        price: number;
        title: string;
        title_original: string;
        episodeTitle: string;
        shortDescription: string;
        episode_title: string;
        episode_title_original: string;
        imdbRating: number;
        image: string;
        background_image_url: string;
        kind: string;
        vod_series_id: string;
        season_number: number;
        episode_number: number;
        category: string;
        genres: string[];
        release_year: number;
        duration: number;
        production_countries: string[];
        max_quality: string;
        audio_languages: string[];
        subtitle_languages: string[];
        cast: Person[] | Credit[];
    };

export type SeriesDetails<T extends DetailEpisode | TVSeriesDetailEpisode> = StandardAsset & {
    seasons?: Season<T>[];
    description?: string;
    image_url?: string;
    credits?: Credit[];
    cast?: Person[];
    genres?: string[];
    releaseYear?: string;
    productionCountries?: string[];
    category?: string;
    episodeNumber?: number;
    episode_title?: string;
    seasonNumber?: number;
    imdbRating?: number;
    programSeriesId?: string;
    program_series_id?: string;
    listings?: Listing[];
};

export type ProgramDetails = {
    credits: { role: string; person: any }[];
    id: string;
    title: string;
    episode_title: string;
    episodeTitle: string;
    longDescription: string;
    description: string;
    tagline: string;
    image_url: string;
    kind: string;
    program_series_id: string;
    season_number: number;
    episode_number: number;
    category: string;
    genres: string[];
    release_year: number;
    production_countries: string[];
    channel_id: string;
    imdb_rating: number;
    series_recording_allowed: boolean;
    recording_until: number;
    program: Program;
    listings: {
        id: string;
        channel_id: string;
        start: number;
        duration: number;
        recording_until: number;
        replay_until: number;
        restart_until: number;
        series_recording_allowed: boolean;
    }[];
} & StandardAsset &
    AiringTime &
    Subscribable &
    Recordable &
    Bookmark & {
        progType: string;
        channelId: string;
        channelNumber: number;
        channelName: string;
        channelLogo: string;
        isHDChannel: boolean;
        resolution: ResolutionType;
        isChannelSubscribed: boolean;
        description: string;
        cast: Person[];
        subTitle?: string;
        episode_name?: string;
        releaseYear?: string;
        countries: string[];
        genres?: any[];
        seasonId?: string;
        seriesId?: string;
        channelHasReplay: boolean;
        isFavorite: boolean;
        isMovie: boolean;
        worldId: string;
        isLive?: boolean;
        episodeTitleShort?: string;
        playOptions: PlayOptionsData;
        additional_services_available?: boolean;
        // Raw content data for getContentId
        contents?: any[];
        rawProgramData?: any;
    };

export type RelatedContent = {
    programs: ProgramDetails[];
    program_series: SeriesDetails<TVSeriesDetailEpisode>[];
    vod_assets: VodPlayerAssetInfo[];
    vod_series: SeriesDetails<DetailEpisode>[];
};

export interface NowAndNextProgram {
    id: string;
    channel_id: string;
    start: number;
    duration: number;
    outdated: boolean;
    program: {
        id: string;
        title: string;
        title_original: string;
        episode_title: string;
        episode_title_original: string;
        description: string;
        tagline: string;
        image_url: string;
        background_image_url: string;
        kind: string;
        program_series_id: string | null;
        season_number: number | null;
        episode_number: number | null;
        category: string;
        genres: string[];
        release_year: number;
        production_countries: string[];
        imdb_rating: number | null;
    };
}

export interface ChannelPrograms {
    [channelId: string]: NowAndNextProgram[];
}

export interface NowAndNextApiResponse {
    results: NowAndNextProgram[];
    next_request_in: number;
}

export interface NowAndNextData {
    currentPrograms: NowAndNext[];
    channelPrograms: ChannelPrograms;
    lastUpdated: number;
    nextUpdateIn: number;
}

export type RecordingDetails = Omit<ProgramDetails, (keyof Subscribable & 'playOptions') | 'worldId' | 'isFavorite' | 'resolution'>;

export type ExtraMetaData = {
    imdRating: number;
    actors?: Person[];
    directors?: Person[];
};

export enum defaultLanguageTypes {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

export type SeriesApiResponse = {
    id: string;
    title: string;
    title_original?: string;
    description: string;
    tagline: string;
    image_url: string;
    background_image_url: string;
    category: string;
    genres: string[];
    release_year: number;
    production_countries: string[];
    credits: Credit[];
    imdb_rating: number | null;
    episode_title?: string;
    episode_number?: number | null;
    season_number?: number | null;
    kind?: string;
    program_series_id?: string | null;
    listings?: SeriesEpisodeListing[];
    has_trailer?: boolean;
};

export type SeriesEpisodeListing = {
    id: string;
    channel_id: string;
    start: number;
    duration: number;
    recording_until: number;
    replay_until: number;
    restart_until: number;
    adult_content?: boolean;
    series_recording_allowed: boolean;
    outdated?: boolean;
    additional_services_available?: boolean;
};

export type SeriesEpisodeApiResponse = {
    id: string;
    title: string;
    title_original?: string;
    episode_title: string;
    episode_title_original?: string;
    description: string;
    tagline: string;
    image_url: string;
    background_image_url: string;
    kind: string;
    category: string;
    genres: string[];
    release_year: number;
    production_countries: string[];
    credits: Credit[];
    imdb_rating: number | null;
    season_number: number;
    episode_number: number;
    duration: number;
    has_trailer: boolean;
    audio_languages?: string[];
    program_series_id?: string | null;
    listings?: SeriesEpisodeListing[];
    vod_series_id?: string;
    vod_terms?: VodTerm[];
    vod_session_until?: number | null;
    additional_services_available?: boolean;
};

export type SeriesEpisodesApiResponse = {
    results: SeriesEpisodeApiResponse[];
};
